import type { Color, Square } from '../../game/types.js';
import { sameSquare } from '../../game/types.js';
import {
  GHOST_PIECE_ALPHA,
  HOVER_GLOW_BLUR,
  MANDATORY_PULSE_PERIOD_MS,
  PIECE_RADIUS,
  SQUARE_SIZE,
} from '../../utils/constants.js';
import { squareToCanvasCenter, squareToCanvasRect, type ViewerRotation } from '../geometry.js';
import type { RenderPalette } from '../palette.js';
import { drawPiece } from '../PieceRenderer.js';

/**
 * Snapshot of UI interaction state passed each frame. Kept deliberately
 * minimal — the Phase 4 MoveSelector will fill this in. The overlay is a
 * pure renderer: it owns no state, just draws what it is told.
 */
export interface InteractionSnapshot {
  readonly currentPlayer: Color;
  readonly hovered: Square | null;
  readonly selected: Square | null;
  readonly dragPosition: { readonly x: number; readonly y: number } | null;
  readonly draggedKind: 'man' | 'king' | null;
  /** Squares the selected piece may move to right now. */
  readonly validDestinations: readonly Square[];
  /** Opponent pieces that would be removed by selecting a hovered destination. */
  readonly captureTargets: readonly Square[];
  /** Pieces that have a mandatory capture available — pulse to draw the eye. */
  readonly mandatorySources: readonly Square[];
  readonly timeMs: number;
}

export function drawInteractionOverlay(
  ctx: CanvasRenderingContext2D,
  snapshot: InteractionSnapshot,
  palette: RenderPalette,
  rotation: ViewerRotation = 0,
): void {
  drawMandatoryPulses(ctx, snapshot.mandatorySources, snapshot.timeMs, palette, rotation);

  if (snapshot.selected) {
    drawSelectedRing(ctx, snapshot.selected, palette, rotation);
  }

  if (snapshot.hovered && !snapshot.dragPosition) {
    drawHoverHighlight(ctx, snapshot.hovered, palette, rotation);
  }

  if (snapshot.validDestinations.length > 0) {
    drawValidDestinations(
      ctx,
      snapshot.validDestinations,
      snapshot.hovered,
      palette,
      rotation,
    );
  }

  if (snapshot.captureTargets.length > 0) {
    drawCaptureTargets(ctx, snapshot.captureTargets, palette, rotation);
  }

  if (snapshot.dragPosition && snapshot.draggedKind) {
    drawDragGhost(ctx, snapshot, palette, rotation);
  }
}

function drawMandatoryPulses(
  ctx: CanvasRenderingContext2D,
  sources: readonly Square[],
  timeMs: number,
  palette: RenderPalette,
  rotation: ViewerRotation,
): void {
  if (sources.length === 0) return;
  // 0..1..0 over the period.
  const phase = (timeMs % MANDATORY_PULSE_PERIOD_MS) / MANDATORY_PULSE_PERIOD_MS;
  const pulse = 0.5 - 0.5 * Math.cos(phase * Math.PI * 2);
  const alpha = 0.4 + pulse * 0.4;
  const radius = PIECE_RADIUS + 3 + pulse * 4;

  ctx.save();
  ctx.lineWidth = 2.5;
  for (const sq of sources) {
    const { x, y } = squareToCanvasCenter(sq, rotation);
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.strokeStyle = withAlpha(palette.mandatoryPulse, alpha);
    ctx.stroke();
  }
  ctx.restore();
}

function drawSelectedRing(
  ctx: CanvasRenderingContext2D,
  square: Square,
  palette: RenderPalette,
  rotation: ViewerRotation,
): void {
  const { x, y } = squareToCanvasCenter(square, rotation);
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, PIECE_RADIUS + 4, 0, Math.PI * 2);
  ctx.strokeStyle = palette.selectedRing;
  ctx.lineWidth = 3;
  ctx.shadowColor = palette.selectedRing;
  ctx.shadowBlur = 10;
  ctx.stroke();
  ctx.restore();
}

function drawHoverHighlight(
  ctx: CanvasRenderingContext2D,
  square: Square,
  palette: RenderPalette,
  rotation: ViewerRotation,
): void {
  const rect = squareToCanvasRect(square, rotation);
  ctx.save();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.07)';
  ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
  ctx.shadowBlur = HOVER_GLOW_BLUR;
  ctx.shadowColor = palette.hoverGlow;
  ctx.strokeStyle = palette.hoverGlow;
  ctx.lineWidth = 2;
  ctx.strokeRect(rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2);
  ctx.restore();
}

function drawValidDestinations(
  ctx: CanvasRenderingContext2D,
  destinations: readonly Square[],
  hovered: Square | null,
  palette: RenderPalette,
  rotation: ViewerRotation,
): void {
  ctx.save();
  for (const dest of destinations) {
    const { x, y } = squareToCanvasCenter(dest, rotation);
    const isHoveredDest = hovered && sameSquare(hovered, dest);
    if (isHoveredDest) {
      ctx.beginPath();
      ctx.arc(x, y, PIECE_RADIUS + 2, 0, Math.PI * 2);
      ctx.fillStyle = withAlpha(palette.validDestination, 0.35);
      ctx.fill();
      ctx.strokeStyle = palette.validDestination;
      ctx.lineWidth = 2.5;
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(x, y, SQUARE_SIZE * 0.16, 0, Math.PI * 2);
      ctx.fillStyle = withAlpha(palette.validDestination, 0.7);
      ctx.fill();
    }
  }
  ctx.restore();
}

function drawCaptureTargets(
  ctx: CanvasRenderingContext2D,
  targets: readonly Square[],
  palette: RenderPalette,
  rotation: ViewerRotation,
): void {
  ctx.save();
  ctx.strokeStyle = palette.captureTarget;
  ctx.lineWidth = 3;
  ctx.setLineDash([6, 4]);
  for (const sq of targets) {
    const { x, y } = squareToCanvasCenter(sq, rotation);
    ctx.beginPath();
    ctx.arc(x, y, PIECE_RADIUS + 4, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

function drawDragGhost(
  ctx: CanvasRenderingContext2D,
  snapshot: InteractionSnapshot,
  palette: RenderPalette,
  rotation: ViewerRotation,
): void {
  if (!snapshot.dragPosition || !snapshot.draggedKind) return;

  // Ghost the source square so it's clear the piece left.
  if (snapshot.selected) {
    const src = squareToCanvasCenter(snapshot.selected, rotation);
    ctx.save();
    ctx.globalAlpha = GHOST_PIECE_ALPHA;
    drawPiece(
      ctx,
      src.x,
      src.y,
      snapshot.currentPlayer,
      snapshot.draggedKind,
      palette,
    );
    ctx.restore();
  }

  // Render the actively dragged piece slightly lifted.
  drawPiece(
    ctx,
    snapshot.dragPosition.x,
    snapshot.dragPosition.y,
    snapshot.currentPlayer,
    snapshot.draggedKind,
    palette,
    { scale: 1.08, shadowLift: 6 },
  );
}

/**
 * Apply an alpha override to a CSS color. Supports `rgba(r, g, b, a)` and
 * `#rrggbb`. Used so the palette can hold "design intent" alphas while the
 * overlay multiplies in transient ones (pulse, hover).
 */
function withAlpha(color: string, alpha: number): string {
  const rgbaMatch = color.match(/^rgba?\(([^)]+)\)$/);
  if (rgbaMatch && rgbaMatch[1]) {
    const parts = rgbaMatch[1].split(',').map((s) => s.trim());
    if (parts.length >= 3) {
      const [r, g, b] = parts;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
  if (color.startsWith('#') && (color.length === 7 || color.length === 4)) {
    const hex =
      color.length === 4
        ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
        : color;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
}
